import React, { useState, useEffect } from 'react';
import Breadcrumb from './Breadcrumb';
import Modal from './Modal';
import ModalInfo from './ModalInfo';
import '../styles/Profile.css';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { getPasswordRules, isPasswordValid, isValidEmail, isValidPhone } from '../helpers/validationHelper';
import { updateCoCoProfile } from '../helpers/apiHelper';  
import { isMaskedPhone } from '../helpers/phoneHelper';
import InternationalPhoneInput from './InternationalPhoneInput';
import SignInMethodSettings from './SignInMethodSettings';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import '../styles/Views.css';
import { useNavigate } from 'react-router-dom';
import { ChangePasswordCommand, GlobalSignOutCommand } from '@aws-sdk/client-cognito-identity-provider';
import { cognitoClient } from '../aws/cognitoClient';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Profile = () => {
    const { t } = useTranslation();
    const { user, setUser, tokens, signOut } = useAuth();
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [loadingPhone, setLoadingPhone] = useState(false);
    const [loadingPasswordChange, setLoadingPasswordChange] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(true);
    const initialPhone = user.phone_number || user.phone || '';
    const [email, setEmail] = useState(user.email || '');
    const [phone, setPhone] = useState(initialPhone);
    const [isPhoneUpdateStarted, setIsPhoneUpdateStarted] = useState(
        Boolean(initialPhone) && !isMaskedPhone(initialPhone)
    );
    const [phoneUpdateAttempted, setPhoneUpdateAttempted] = useState(false);
    const token = tokens.idToken;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [modalUpdateType, setModalUpdateType] = useState(null); // 'email' or 'phone' or null
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const navigate = useNavigate();
    const { features } = useFeatureFlags();
    // Sign-in method + Face ID card (runtime `signInModes`); off keeps the PROD password change.
    const signInModesEnabled = features?.signInModes === true;
    const passwordRules = getPasswordRules(newPassword);
    const passwordsMatch = newPassword.length > 0 && newPassword === confirmNewPassword;

    const handleBack = () => {
        navigate('/');
    };

    useEffect(() => {
         setEmail(user.email || '');
         const nextPhone = user.phone_number || user.phone || '';
         setPhone(nextPhone);
         setIsPhoneUpdateStarted(Boolean(nextPhone) && !isMaskedPhone(nextPhone));
         setPhoneUpdateAttempted(false);
    }, [user]);

    const handlePhoneUpdate = async () => {
        setPhoneUpdateAttempted(true);
        if (!isValidPhone(phone)) return;

        setLoadingPhone(true);
        setErrorMessage('');
        try {
            await updateCoCoProfile(user.cocoId, user.email, '', phone, token);
            setUser({ ...user, phone, phone_number: phone });
            setPhoneUpdateAttempted(false);
            setSuccessMessage(t('profile.phoneUpdated'));
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Failed to update profile phone:', error);
            setErrorMessage(t('profile.updateFailed'));
        } finally {
            setLoadingPhone(false);
        }
    }

    const handleEmailUpdate = async () => {
        setLoadingEmail(true);
        setErrorMessage('');
        try {
            await updateCoCoProfile(user.cocoId, user.email, email, '', token);
            setUser({ ...user, email });
            setSuccessMessage(t('profile.emailUpdated'));
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Failed to update profile email:', error);
            setErrorMessage(
                error.code === 'PROFILE_EMAIL_IN_USE'
                    ? t('profile.emailAlreadyInUse')
                    : t('profile.updateFailed')
            );
        } finally {
            setLoadingEmail(false);
        }
    }

    const handleEmailChange = (val) => {
        setEmail(val);
        setIsEmailValid(isValidEmail(val));
    };
    
    const handlePhoneChange = (val) => {
        setPhone(val);
        setIsPhoneUpdateStarted(true);
        if (isValidPhone(val)) {
            setPhoneUpdateAttempted(false);
        }
    };

    const handlePhoneUpdateRequest = () => {
        setPhoneUpdateAttempted(true);
        if (!isValidPhone(phone)) return;

        setModalUpdateType('phone');
        setIsModalOpen(true);
    };

    const handlePasswordChange = async () => {
        setErrorMessage('');

        if (!currentPassword || !isPasswordValid(newPassword) || !passwordsMatch) {
            setErrorMessage(t('profile.passwordChangeInvalid'));
            return;
        }

        if (!tokens.accessToken) {
            setErrorMessage(t('profile.passwordChangeFailed'));
            return;
        }

        setLoadingPasswordChange(true);

        try {
            await cognitoClient.send(new ChangePasswordCommand({
                AccessToken: tokens.accessToken,
                PreviousPassword: currentPassword,
                ProposedPassword: newPassword,
            }));

            try {
                await cognitoClient.send(new GlobalSignOutCommand({
                    AccessToken: tokens.accessToken,
                }));
            } catch (error) {
                // The password has already changed. Continue with local session cleanup.
                console.warn('Unable to revoke all Cognito sessions after password change.');
            }

            await signOut({ reason: 'PASSWORD_CHANGED' });
            navigate('/signin', { replace: true, state: { passwordChanged: true } });
        } catch (error) {
            setErrorMessage(
                error.name === 'NotAuthorizedException'
                    ? t('profile.currentPasswordIncorrect')
                    : t('profile.passwordChangeFailed')
            );
        } finally {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setLoadingPasswordChange(false);
        }
    };

    const closePasswordModal = () => {
        if (loadingPasswordChange) return;
        setIsPasswordModalOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setErrorMessage('');
    };

    const openPasswordModal = () => {
        setErrorMessage('');
        setIsPasswordModalOpen(true);
    };

    return (
        <div className="portal-page profile-container">
            <div className="portal-page-breadcrumb">
                <Breadcrumb
                    items={[
                        { label: t('breadcrumb.home'), link: '/' },
                        { label: t('breadcrumb.profile') }
                    ]}
                />
            </div>

            <header className="portal-page-header">
                <p className="portal-page-kicker">{t('breadcrumb.profile')}</p>
                <h2 className='portal-page-title'>{t('profile.title')}</h2>
            </header>

            {successMessage && (
                <div className="portal-feedback success portal-page-shell">
                    {successMessage}
                </div>
            )}

            {errorMessage && (
                <div className="portal-feedback error portal-page-shell" role="alert">
                    {errorMessage}
                </div>
            )}

            <section className="portal-page-panel portal-form-grid">
                <div className="portal-inline-form">
                    <label className="portal-label" htmlFor="email-input">{t('profile.email')}</label>
                    <div className="portal-inline-form-controls">
                        <input
                            id="email-input"
                            type="email"
                            value={email}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            className={`portal-input ${!isEmailValid ? 'input-error' : ''}`}
                        />
                        <button
                            className={`portal-button ${loadingEmail || !isEmailValid ? 'disabled' : ''}`}
                            onClick={() => {
                                setModalUpdateType('email');
                                setIsModalOpen(true);
                            }}
                            disabled={loadingEmail || !isEmailValid}
                        >
                            {loadingEmail ? t('updating') : t('update')}
                        </button>
                    </div>
                </div>

                <div className="portal-inline-form">
                    <label className="portal-label" htmlFor="phone-input">{t('profile.phone')}</label>
                    <div className="portal-inline-form-controls">
                        <InternationalPhoneInput
                            id="phone-input"
                            value={phone}
                            onChange={handlePhoneChange}
                            invalid={phoneUpdateAttempted && !isValidPhone(phone)}
                        />
                        <button
                            className={`portal-button ${loadingPhone || !isPhoneUpdateStarted ? 'disabled' : ''}`}
                            onClick={handlePhoneUpdateRequest}
                            disabled={loadingPhone || !isPhoneUpdateStarted}
                        >
                            {loadingPhone ? t('updating') : t('update')}
                        </button>
                    </div>
                </div>

                {!signInModesEnabled && (
                <div className="portal-inline-form">
                    <div>
                        <p className="portal-label">{t('profile.password')}</p>
                        <p>{t('profile.passwordChangeDescription')}</p>
                    </div>
                    <div className="portal-inline-form-controls">
                        <button className="portal-button" onClick={openPasswordModal}>
                            {t('profile.changePassword')}
                        </button>
                    </div>
                </div>
                )}
            </section>

            {signInModesEnabled && <SignInMethodSettings />}

            <Modal isOpen={isPasswordModalOpen} onClose={closePasswordModal}>
                <section className="password-change-modal">
                    <h3>{t('profile.changePassword')}</h3>
                    <p>{t('profile.passwordChangeDescription')}</p>

                    {errorMessage && <div className="password-change-error" role="alert">{errorMessage}</div>}

                    <div className="password-field">
                        <input
                            id="current-password-input"
                            type={showCurrentPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder={t('profile.currentPassword')}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="portal-input"
                        />
                        <span className="password-toggle-icon" onClick={() => setShowCurrentPassword((prev) => !prev)} role="button" aria-label={showCurrentPassword ? t('hide') : t('show')}>
                            {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>
                    <div className="password-field">
                        <input
                            id="new-password-input"
                            type={showNewPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={t('signIn.newPasswordPlaceholder')}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="portal-input"
                        />
                        <span className="password-toggle-icon" onClick={() => setShowNewPassword((prev) => !prev)} role="button" aria-label={showNewPassword ? t('hide') : t('show')}>
                            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>
                    <div className="password-field">
                        <input
                            id="confirm-new-password-input"
                            type={showConfirmNewPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={t('signIn.confirmNewPasswordPlaceholder')}
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            className="portal-input"
                        />
                        <span className="password-toggle-icon" onClick={() => setShowConfirmNewPassword((prev) => !prev)} role="button" aria-label={showConfirmNewPassword ? t('hide') : t('show')}>
                            {showConfirmNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>

                    {confirmNewPassword.length > 0 && (
                        <p className={`password-match ${passwordsMatch ? 'valid' : 'invalid'}`}>
                            {passwordsMatch ? t('signIn.passwordsMatch') : t('signIn.passwordsDoNotMatch')}
                        </p>
                    )}

                    <div className="resetPasswordLines">
                        <p className="resetPasswordLineTitle">{t('signIn.newPasswordLineTitle')}</p>
                        <p className={`resetPasswordLine ${passwordRules.minLength ? 'valid' : ''}`}>{t('signIn.newPasswordLine1')}</p>
                        <p className={`resetPasswordLine ${passwordRules.hasNumber ? 'valid' : ''}`}>{t('signIn.newPasswordLine2')}</p>
                        <p className={`resetPasswordLine ${passwordRules.hasSpecialChar ? 'valid' : ''}`}>{t('signIn.newPasswordLine3')}</p>
                        <p className={`resetPasswordLine ${passwordRules.hasUppercase ? 'valid' : ''}`}>{t('signIn.newPasswordLine4')}</p>
                        <p className={`resetPasswordLine ${passwordRules.hasLowercase ? 'valid' : ''}`}>{t('signIn.newPasswordLine5')}</p>
                    </div>

                    <button
                        className={`portal-button password-change-submit ${loadingPasswordChange ? 'disabled' : ''}`}
                        onClick={handlePasswordChange}
                        disabled={loadingPasswordChange || !currentPassword || !isPasswordValid(newPassword) || !passwordsMatch}
                    >
                        {loadingPasswordChange ? t('loading') : t('profile.changePassword')}
                    </button>
                </section>
            </Modal>

            {/* Single shared modal */}
            <ModalInfo
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setModalUpdateType(null);
                }}
                onAction={(action) => {
                    if (action === 'update') {
                        if (modalUpdateType === 'email') {
                            handleEmailUpdate();
                        } else if (modalUpdateType === 'phone') {
                            handlePhoneUpdate();
                        }
                        setIsModalOpen(false);
                        setModalUpdateType(null);
                    }
                }}
                updateType={modalUpdateType}
            />

            <div onClick={handleBack} className='link mt-20 portal-page-footer-link'>{t("back")}</div>
        </div>
    );
}

export default Profile;
